import { ChevronDownIcon } from "@chakra-ui/icons";
import { BoxProps, IconButton, useDisclosure } from "@chakra-ui/react";
import { ReactNode, createContext, useContext, useId } from "react";

import { useOnlyOnce, useResizeObserver, useUpdate } from "@nens/react/hooks";
import { useThrottledCallback } from "@nens/shared-utils";

import { RanaTable } from "../rana-table";

import { TableRow, TableRowProps } from "./table-row";

type TableRowAccordionContextValue = {
  isOpen: boolean;
  onToggle: () => void;
};

const TableRowAccordionContext = createContext<TableRowAccordionContextValue | undefined>(
  undefined
);

const useTableRowAccordion = () => {
  const context = useContext(TableRowAccordionContext);
  if (!context) {
    throw new Error("useTableRowAccordion must be used within TableRowAccordion");
  }

  return context;
};

type TableRowAccordionProps = {
  defaultIsOpen?: boolean;
  children: ReactNode;
};

export const TableRowAccordion = ({ defaultIsOpen = false, children }: TableRowAccordionProps) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen });
  return (
    <TableRowAccordionContext.Provider value={{ isOpen, onToggle }}>
      {children}
    </TableRowAccordionContext.Provider>
  );
};

interface AccordionRowProps extends Omit<TableRowProps, "onClick"> {
  children: ReactNode;
}

const TableRowButton = ({ children, ...rest }: AccordionRowProps) => {
  const { onToggle, isOpen } = useTableRowAccordion();
  return (
    <TableRow onClick={onToggle} {...rest}>
      <RanaTable.Cell>
        {!rest.isDisabled && (
          <IconButton
            aria-label="Toggle accordion"
            icon={
              <ChevronDownIcon
                transform={`rotate(${isOpen ? 180 : 0}deg)`}
                transition="transform 0.2s ease"
              />
            }
            variant="ghost"
            minW="fit"
          />
        )}
      </RanaTable.Cell>
      {children}
    </TableRow>
  );
};

TableRowAccordion.Button = TableRowButton;

const TableRowAccordionPanelSimple = ({ children, ...rest }: AccordionRowProps) => {
  const { isOpen } = useTableRowAccordion();

  const collapseProps: BoxProps = !isOpen
    ? {
        h: 0,
        opacity: 0,
        pointerEvents: "none",
        borderBottom: "none",
        overflow: "hidden",
      }
    : {};

  return (
    <TableRow transition="height 0.2s ease, opacity 0.15s ease" {...rest} {...collapseProps}>
      <RanaTable.Cell />
      {/* gridColumn "2 / -1": start at column 2 (after the checkbox), span to the last column */}
      <RanaTable.Cell gridColumn="2 / -1">{children}</RanaTable.Cell>
    </TableRow>
  );
};

const getElHeight = (element: Element | HTMLElement) =>
  (element as HTMLElement).getBoundingClientRect().height;

// When row height="fit", CSS cannot animate from 0 to fit.
// So on open, we set the row height to the measured tallest cell, and when closed to 0.
// We also set td height to "fit" via sx to prevent a resize feedback loop.
const TableRowAccordionPanelHeightIsDynamic = ({ children, ...rest }: AccordionRowProps) => {
  const id = useId();
  const row = document.getElementById(id);
  const rowChildren = Array.from(row?.children ?? []) as HTMLElement[];
  const largestChild = rowChildren.reduce(
    (largest, child) => (!largest || getElHeight(child) > getElHeight(largest) ? child : largest),
    undefined as HTMLElement | undefined
  );

  // Update the row height when it changes size
  const update = useThrottledCallback(useUpdate(), 100);
  useOnlyOnce(update);
  useResizeObserver(update, largestChild);

  const rowHeight = largestChild ? getElHeight(largestChild) : undefined;

  const unCollapsedProps: BoxProps = rowHeight
    ? { h: rowHeight + "px", sx: { td: { h: "fit" } } }
    : {};

  return (
    <TableRowAccordionPanelSimple id={id} {...rest} {...unCollapsedProps}>
      {children}
    </TableRowAccordionPanelSimple>
  );
};

const TableRowAccordionPanel = ({ children, ...rest }: AccordionRowProps) => {
  const heightIsDynamic = rest.h === "fit" || rest.height === "fit";

  if (heightIsDynamic) {
    return (
      <TableRowAccordionPanelHeightIsDynamic {...rest}>
        {children}
      </TableRowAccordionPanelHeightIsDynamic>
    );
  }

  return <TableRowAccordionPanelSimple {...rest}>{children}</TableRowAccordionPanelSimple>;
};

TableRowAccordion.Panel = TableRowAccordionPanel;
